import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import attendanceAPI from "services/attendanceAPI";
import employeeAPI from "services/employeeAPI";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import EmployeeProfileImage from "../../../components/navbar/EmployeeProfileImage";
import { FaCalendarAlt } from "react-icons/fa";
import { useAuth } from "contexts/AuthContext";

const DatePickerPortal = ({ children }) => {
  if (typeof document === "undefined") return children;
  return createPortal(
    <div style={{ position: "relative", zIndex: 9999 }}>{children}</div>,
    document.body
  );
};

const renderMonthContent = (month, shortMonth, year) => (
  <span>{shortMonth}</span>
);

const getDefaultEndMonth = () => {
  const today = new Date();
  const d = today.getDate();
  const y = today.getFullYear();
  const m = today.getMonth();
  return d > 25 ? new Date(y, m + 1, 1) : new Date(y, m, 1);
};

const getCycleBounds = (endMonthDate) => {
  const endYear = endMonthDate.getFullYear();
  const endMonth = endMonthDate.getMonth();
  const fromDate = new Date(endYear, endMonth - 1, 26);
  const toDate = new Date(endYear, endMonth, 25);
  return { fromDate, toDate, apiMonth: endMonth, apiYear: endYear };
};

const getEmptySelectedMatrixSummary = () => ({
  date: "Select a matrix box",
  punchIn: "—",
  punchOut: "—",
  totalHours: "—",
  status: "Not Selected",
  statusKey: "idle",
});

const AttendanceMatrix = () => {
  const { user, isSuperAdmin } = useAuth();
  const [matrix, setMatrix] = useState([]);
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(getDefaultEndMonth);
  const [selectedMatrixSummary, setSelectedMatrixSummary] = useState(
    getEmptySelectedMatrixSummary
  );
  const [selectedCellKey, setSelectedCellKey] = useState("");
  const [shouldFilterToEmployee, setShouldFilterToEmployee] = useState(
    !isSuperAdmin
  );

  const employeeKeys = useMemo(
    () =>
      new Set(
        [
          user,
          localStorage.getItem("employee_id"),
          localStorage.getItem("emp_id"),
          localStorage.getItem("user_id"),
        ]
          .filter(
            (value) => value !== null && value !== undefined && value !== ""
          )
          .map((value) => String(value))
      ),
    []
  );

  const pad = (n) => n.toString().padStart(2, "0");
  const fmtDate = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const showEmployeePunchDetails = !isSuperAdmin;

  useEffect(() => {
    if (isSuperAdmin) {
      setShouldFilterToEmployee(false);
      return;
    }

    const profileId =
      localStorage.getItem("employee_id") ||
      localStorage.getItem("user_id") ||
      localStorage.getItem("emp_id") ||
      user;

    if (!profileId) {
      setShouldFilterToEmployee(true);
      return;
    }

    employeeAPI.getEmployeeById(
      profileId,
      (data) => {
        const profile = data?.data || data?.employee || data?.user || data;
        const roleLabel = String(
          profile?.role_name ||
            profile?.designation ||
            profile?.role?.role_name ||
            profile?.role ||
            ""
        ).toLowerCase();

        setShouldFilterToEmployee(!roleLabel.includes("admin"));
      },
      () => {
        setShouldFilterToEmployee(true);
      }
    );
  }, [isSuperAdmin, user]);

  const formatDateKey = (dateValue) => {
    if (!dateValue) return "Select a matrix box";
    const [year, month, day] = String(dateValue).split("-").map(Number);
    const parsedDate =
      year && month && day
        ? new Date(year, month - 1, day)
        : new Date(dateValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return String(dateValue);
    }

    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatPunchTime = (timeStr) => {
    if (!timeStr) return "—";
    if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr;
    if (/^\d{2}:\d{2}:\d{2}$/.test(timeStr)) return timeStr.slice(0, 5);
    if (/\d{2}:\d{2}:\d{2}/.test(timeStr)) return timeStr.slice(0, 5);
    const d = new Date(timeStr);
    if (!isNaN(d)) return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return timeStr;
  };

  const renderPunchTimes = (rec, defaultClassName, compact = false) => {
    if (!rec?.punch_in) {
      return null;
    }
    const punchInClassName =
      rec?.punch_in && rec.punch_in >= "09:46:00"
        ? "text-red-500 dark:text-red-400"
        : defaultClassName;

    return (
      <div
        className={punchInClassName}
        style={{
          fontSize: compact ? 9 : 10,
          marginTop: compact ? 1 : 2,
          fontWeight: 700,
          lineHeight: 1.2,
        }}
      >
        {formatPunchTime(rec.punch_in)}
      </div>
    );
  };

  const getStatusMeta = (rec) => {
    const status = rec?.status?.toLowerCase() || "";

    if (!rec) {
      return {
        label: "No Record",
        badgeClass:
          "bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-gray-300",
      };
    }
    if (rec.is_holiday === true && status === "present") {
      return {
        label: "Holiday Duty",
        badgeClass:
          "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200",
      };
    }
    if (rec.is_holiday === true) {
      return {
        label: "Holiday",
        badgeClass:
          "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200",
      };
    }
    if (rec.is_weekoff === true && status === "present") {
      return {
        label: "Week Off Duty",
        badgeClass:
          "bg-amber-100 text-amber-700 dark:bg-amber-600/35 dark:text-amber-100",
      };
    }
    if (rec.is_weekoff === true) {
      return {
        label: "Week Off",
        badgeClass:
          "bg-amber-100 text-amber-700 dark:bg-amber-600/35 dark:text-amber-100",
      };
    }
    if (status === "present") {
      return {
        label: "Present",
        badgeClass:
          "bg-emerald-100 text-emerald-700 dark:bg-emerald-600/35 dark:text-emerald-100",
      };
    }
    if (status === "absent") {
      return {
        label: "Absent",
        badgeClass:
          "bg-rose-100 text-rose-700 dark:bg-rose-600/35 dark:text-rose-100",
      };
    }
    if (status.includes("halfday")) {
      return {
        label: "Half Day",
        badgeClass:
          "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200",
      };
    }

    return {
      label: rec.status || "Unknown",
      badgeClass:
        "bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-gray-300",
    };
  };

  const handleMatrixCellSelect = (rec, date, employeeCode) => {
    const statusMeta = getStatusMeta(rec);

    setSelectedCellKey(`${employeeCode}-${date}`);
    setSelectedMatrixSummary({
      date: formatDateKey(date),
      punchIn: formatPunchTime(rec?.punch_in),
      punchOut: formatPunchTime(rec?.punch_out),
      totalHours: rec?.total_hours || "—",
      status: statusMeta.label,
      statusKey: statusMeta.badgeClass,
    });
  };

  useEffect(() => {
    setLoading(true);
    setSelectedCellKey("");
    setSelectedMatrixSummary(getEmptySelectedMatrixSummary());
    const { fromDate, toDate, apiMonth, apiYear } =
      getCycleBounds(selectedMonth);

    const dateArr = [];
    const cursor = new Date(fromDate);
    while (cursor <= toDate) {
      dateArr.push(fmtDate(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }

    attendanceAPI.getAttendanceRecordsByAdmin(
      { month: apiMonth, year: apiYear },
      (data) => {
        let records = data?.data?.attendance ?? [];

        if (shouldFilterToEmployee && employeeKeys.size > 0) {
          records = records.filter((rec) => {
            const recordKeys = [
              rec.employee,
              rec.emp_id,
              rec.employee_id,
              rec.id,
            ]
              .filter(
                (value) => value !== null && value !== undefined && value !== ""
              )
              .map((value) => String(value));
            return recordKeys.some((value) => employeeKeys.has(value));
          });
        }

        const uniqueDepts = Array.from(
          new Set(records.map((r) => r.department_name).filter(Boolean))
        ).map((name) => ({ department_name: name }));
        setDepartments(uniqueDepts);

        if (selectedDepartment) {
          records = records.filter(
            (r) => r.department_name === selectedDepartment
          );
        }

        const dateSet = new Set(dateArr);
        const empMap = {};

        records.forEach((rec) => {
          if (!dateSet.has(rec.date)) return;
          if (!empMap[rec.emp_code]) {
            empMap[rec.emp_code] = {
              emp_code: rec.emp_code,
              emp_id: rec.emp_id || rec.employee || rec.id || null,
              employee_name: rec.employee_name,
              department: rec.department_name || "",
              gender: rec.gender || "",
              profile_picture: rec.profile_picture || null,
              attendance: {},
            };
          }
          empMap[rec.emp_code].attendance[rec.date] = rec;
        });

        setDates(dateArr);
        setMatrix(Object.values(empMap));
        setLoading(false);
      },
      () => {
        setDates(dateArr);
        setMatrix([]);
        setLoading(false);
      }
    );
  }, [selectedMonth, selectedDepartment, shouldFilterToEmployee, employeeKeys]);

  /* ─── cell base style ─────────────────────────────────────────── */
  const baseCell = {
    minWidth: 44,
    width: 44,
    maxWidth: 44,
    height: 52,
    padding: "4px 2px",
    verticalAlign: "middle",
    textAlign: "center",
  };

  const SNO_COL_WIDTH = 56;
  const EMP_ID_COL_WIDTH = 120;
  const EMP_DETAIL_COL_WIDTH = 280;
  const EMP_ID_LEFT = SNO_COL_WIDTH;
  const EMP_DETAIL_LEFT = SNO_COL_WIDTH + EMP_ID_COL_WIDTH;

  /* ─── render one attendance cell ──────────────────────────────── */
  const renderCell = (rec, date, employeeCode) => {
    const cellKey = `${employeeCode}-${date}`;
    const isSelected = cellKey === selectedCellKey;
    const employeeCellClassName = showEmployeePunchDetails
      ? `cursor-pointer transition hover:brightness-95 ${
          isSelected ? "ring-2 ring-inset ring-sky-500" : ""
        }`
      : "";

    if (!rec)
      return (
        <td
          key={date}
          className="border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800"
          style={baseCell}
        />
      );

    const status = rec.status?.toLowerCase();
    const isLate = rec.punch_in && rec.punch_in >= "09:46:00";

    // ✅ HOLIDAY TRUE
    if (rec.is_holiday === true) {
      if (status === "present") {
        return (
          <td
            key={date}
            className={`border border-gray-200 bg-green-100 dark:border-gray-700 dark:bg-green-900/40 ${employeeCellClassName}`}
            style={baseCell}
            onClick={
              showEmployeePunchDetails
                ? () => handleMatrixCellSelect(rec, date, employeeCode)
                : undefined
            }
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: "#7e22ce",
                lineHeight: 1,
              }}
            >
              H/D
            </div>
            <svg
              width="13"
              height="13"
              viewBox="0 0 16 16"
              fill="none"
              className="inline-block"
            >
              <path
                d="M3.5 8.5l3 3 5.5-6"
                stroke="#15803d"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {renderPunchTimes(rec, "text-blue-800 dark:text-blue-300", true)}
          </td>
        );
      }

      if (status === "absent") {
        return (
          <td
            key={date}
            className={`border border-gray-200 bg-purple-100 dark:border-gray-700 dark:bg-purple-900/40 ${employeeCellClassName}`}
            style={baseCell}
            onClick={
              showEmployeePunchDetails
                ? () => handleMatrixCellSelect(rec, date, employeeCode)
                : undefined
            }
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: "#7e22ce",
                lineHeight: 1,
              }}
            >
              H/D
            </div>
          </td>
        );
      }

      // Holiday with no attendance
      return (
        <td
          key={date}
          className={`border border-gray-200 bg-purple-100 font-semibold text-purple-700 dark:border-gray-700 dark:bg-purple-900/40 dark:text-purple-400 ${employeeCellClassName}`}
          style={{ ...baseCell, fontSize: 11 }}
          onClick={
            showEmployeePunchDetails
              ? () => handleMatrixCellSelect(rec, date, employeeCode)
              : undefined
          }
        >
          H/D
        </td>
      );
    }

    // ✅ WEEK OFF TRUE
    if (rec.is_weekoff === true) {
      if (status === "present") {
        return (
          <td
            key={date}
            className={`border border-gray-200 bg-green-100 dark:border-green-500/40 dark:bg-green-600/30 ${employeeCellClassName}`}
            style={baseCell}
            onClick={
              showEmployeePunchDetails
                ? () => handleMatrixCellSelect(rec, date, employeeCode)
                : undefined
            }
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="inline-block"
            >
              <path
                d="M3.5 8.5l3 3 5.5-6"
                stroke="#15803d"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {renderPunchTimes(rec, "text-blue-800 dark:text-blue-300")}
          </td>
        );
      }
      if (status === "absent") {
        return (
          <td
            key={date}
            className={`border border-gray-200 bg-yellow-100 dark:border-amber-500/40 dark:bg-amber-600/30 ${employeeCellClassName}`}
            style={baseCell}
            onClick={
              showEmployeePunchDetails
                ? () => handleMatrixCellSelect(rec, date, employeeCode)
                : undefined
            }
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: "#d97706",
                lineHeight: 1,
              }}
            >
              W/O
            </div>
          </td>
        );
      }

      // FIX 2: Added missing `return` for week-off fallback
      return (
        <td
          key={date}
          className={`border border-gray-200 bg-yellow-100 font-semibold text-yellow-700 dark:border-amber-500/40 dark:bg-amber-600/30 dark:text-amber-200 ${employeeCellClassName}`}
          style={{ ...baseCell, fontSize: 11 }}
          onClick={
            showEmployeePunchDetails
              ? () => handleMatrixCellSelect(rec, date, employeeCode)
              : undefined
          }
        >
          W/O
        </td>
      );
    }

    // ✅ NORMAL ATTENDANCE
    if (status === "present") {
      return (
        <td
          key={date}
          className={`border border-gray-200 bg-green-100 dark:border-green-500/40 dark:bg-green-600/30 ${employeeCellClassName}`}
          style={baseCell}
          onClick={
            showEmployeePunchDetails
              ? () => handleMatrixCellSelect(rec, date, employeeCode)
              : undefined
          }
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 16 16"
            fill="none"
            className="inline-block"
          >
            <path
              d="M3.5 8.5l3 3 5.5-6"
              stroke="#15803d"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {renderPunchTimes(rec, "text-blue-800 dark:text-blue-300")}
        </td>
      );
    }

    if (status === "absent") {
      return (
        <td
          key={date}
          className={`border border-gray-200 bg-red-100 dark:border-red-500/40 dark:bg-red-600/30 ${employeeCellClassName}`}
          style={baseCell}
          onClick={
            showEmployeePunchDetails
              ? () => handleMatrixCellSelect(rec, date, employeeCode)
              : undefined
          }
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 16 16"
            fill="none"
            className="inline-block"
          >
            <path
              d="M5 5l6 6M11 5l-6 6"
              stroke="#b91c1c"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </td>
      );
    }

    if (status?.includes("halfday")) {
      return (
        <td
          key={date}
          className={`border border-gray-200 bg-orange-100 font-semibold text-orange-700 dark:border-gray-700 dark:bg-orange-900/40 dark:text-orange-400 ${employeeCellClassName}`}
          style={{ ...baseCell, fontSize: 12 }}
          onClick={
            showEmployeePunchDetails
              ? () => handleMatrixCellSelect(rec, date, employeeCode)
              : undefined
          }
        >
          ½L
          {renderPunchTimes(rec, "text-orange-900 dark:text-orange-300")}
        </td>
      );
    }

    return (
      <td
        key={date}
        className={`border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60 ${employeeCellClassName}`}
        style={{ ...baseCell, fontSize: 11 }}
        onClick={
          showEmployeePunchDetails
            ? () => handleMatrixCellSelect(rec, date, employeeCode)
            : undefined
        }
      >
        <span className="text-gray-600 dark:text-gray-400">{rec.status}</span>
        {renderPunchTimes(rec, "text-gray-600 dark:text-gray-400")}
      </td>
    );
  };

  /* ─── search filter ───────────────────────────────────────────── */
  const filteredMatrix = isSuperAdmin
    ? matrix.filter((emp) => {
        const q = search.toLowerCase().trim();
        return (
          emp.emp_code?.toLowerCase().includes(q) ||
          emp.employee_name?.toLowerCase().includes(q) ||
          emp.department?.toLowerCase().includes(q)
        );
      })
    : matrix;

  /* ─── month groups ────────────────────────────────────────────── */
  const monthGroups = [];
  dates.forEach((dateStr) => {
    const d = new Date(dateStr);
    const label = d.toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
    if (
      !monthGroups.length ||
      monthGroups[monthGroups.length - 1].label !== label
    ) {
      monthGroups.push({ label, count: 1 });
    } else {
      monthGroups[monthGroups.length - 1].count += 1;
    }
  });

  const cycleBounds = getCycleBounds(selectedMonth);
  const { fromDate, toDate } = cycleBounds;
  const cycleLabel = `${fmtDate(fromDate)} → ${fmtDate(toDate)}`;
  const todayStr = fmtDate(new Date());
  const selectedStatusBadgeClass = selectedMatrixSummary.statusKey;

  /* ─── render ──────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-white p-3 transition-colors duration-200 dark:bg-gray-900 sm:p-5">
      <div
        className={`mb-5 ${
          showEmployeePunchDetails
            ? "flex flex-col gap-3 lg:flex-row lg:items-end"
            : ""
        }`}
      >
        {/* ── Filters ── */}
        <div
          className={`grid gap-3 ${
            isSuperAdmin
              ? "sm:grid-cols-3"
              : "w-full sm:max-w-[280px] sm:grid-cols-1 lg:min-w-[280px]"
          }`}
        >
          {isSuperAdmin && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Search
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, ID or department..."
                className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              />
            </div>
          )}

          {/* Month picker */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Month
            </label>
            <DatePicker
              selected={selectedMonth}
              onChange={(date) =>
                setSelectedMonth(
                  new Date(date.getFullYear(), date.getMonth(), 1)
                )
              }
              renderMonthContent={renderMonthContent}
              showMonthYearPicker
              dateFormat="MMMM yyyy"
              popperContainer={DatePickerPortal}
              popperProps={{ strategy: "fixed" }}
              popperPlacement="top-end"
              wrapperClassName="w-full"
              calendarClassName="dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
              customInput={
                <div className="relative flex w-full min-w-[190px] cursor-pointer items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-3 shadow-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 sm:min-w-[230px] lg:min-w-[260px]">
                  <span className="flex-1 select-none whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-200">
                    {selectedMonth
                      ? selectedMonth.toLocaleString("default", {
                          month: "long",
                          year: "numeric",
                        })
                      : "Select month and year"}
                  </span>
                  <FaCalendarAlt className="ml-2 shrink-0 text-gray-400 dark:text-gray-500" />
                </div>
              }
            />
          </div>

          {isSuperAdmin && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Department
              </label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                <option value="">All Departments</option>
                {departments.map((dep) => (
                  <option key={dep.department_name} value={dep.department_name}>
                    {dep.department_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {showEmployeePunchDetails ? (
          <div className="border-slate-200 flex w-full flex-wrap items-center gap-3 rounded-2xl border bg-white/90 px-4 py-3 text-xs font-semibold text-gray-600 shadow-sm dark:border-gray-700 dark:bg-navy-900/90 dark:text-gray-300 lg:ml-auto lg:w-auto lg:min-w-[720px] lg:justify-between">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="inline-block h-3 w-3 rounded border border-green-400 bg-green-200 dark:border-green-300 dark:bg-green-500" />
                Present
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="inline-block h-3 w-3 rounded border border-red-400 bg-red-200 dark:border-red-300 dark:bg-red-500" />
                Absent
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="inline-block h-3 w-3 rounded border border-yellow-400 bg-yellow-200 dark:border-amber-300 dark:bg-amber-500" />
                Week Off
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="inline-block h-3 w-3 rounded border border-purple-400 bg-purple-200 dark:border-purple-500 dark:bg-purple-800" />
                Holiday
              </span>
            </div>

            <div className="ml-auto flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 shadow-sm dark:border-gray-700 dark:bg-navy-800 dark:text-gray-200">
              <FaCalendarAlt className="text-[11px]" />
              <span className="whitespace-nowrap">
                {formatDateKey(cycleBounds.fromDate)} {" -  "}
                {formatDateKey(cycleBounds.toDate)}
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {showEmployeePunchDetails ? (
        <div className="mb-4">
          <div className="border-slate-200 flex flex-wrap items-center gap-2 rounded-2xl border bg-white/90 px-3 py-2 shadow-sm dark:border-gray-700 dark:bg-navy-900/90">
            <div className="rounded-full bg-blue-600 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
              Selected
            </div>

            <div className="bg-sky-50 dark:bg-sky-900/20 flex items-center gap-1 rounded-full px-2.5 py-1">
              <div className="rounded-full bg-purple-500  px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
                <span className="text-sky-600 dark:text-sky-300 text-[10px] font-bold">
                  Date:
                </span>
                <span className="text-slate-700 text-[10px] font-semibold dark:text-white">
                  {selectedMatrixSummary.date}
                </span>
              </div>
            </div>

            <div className="bg-emerald-50 dark:bg-emerald-900/20 flex items-center gap-1 rounded-full px-2.5 py-1">
              <div className="rounded-full bg-green-500 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
                <span className="text-emerald-600 dark:text-emerald-300 text-[10px] font-bold">
                  In:
                </span>
                <span className="text-slate-700 text-[10px] font-semibold dark:text-white">
                  {selectedMatrixSummary.punchIn}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 dark:bg-amber-900/20">
              <div className="rounded-full bg-red-500 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
                <span className="text-[10px] font-bold text-white dark:text-white">
                  Out:
                </span>
                <span className="text-slate-700 text-[10px] font-semibold dark:text-white">
                  {selectedMatrixSummary.punchOut}
                </span>
              </div>
            </div>

            <div className="bg-violet-50 dark:bg-violet-900/20 flex items-center gap-1 rounded-full px-2.5 py-1">
              <div className="rounded-full bg-indigo-600 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
                <span className="text-violet-600 dark:text-violet-300 text-[10px] font-bold ">
                  Total Hours:
                </span>
                <span className="text-slate-700 text-[10px] font-semibold dark:text-white">
                  {selectedMatrixSummary.totalHours}
                </span>
              </div>
            </div>
            <div className="bg-violet-50 dark:bg-violet-900/20 flex items-center gap-1 rounded-full px-2.5 py-1">
              <div className="rounded-full bg-teal-700 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
                <span className="text-violet-600 dark:text-violet-300 text-[10px] font-bold ">
                  Status:
                </span>
                <span className="text-slate-700 text-[10px] font-semibold dark:text-white">
                  {selectedMatrixSummary.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {!showEmployeePunchDetails ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-1 rounded-xl border border-blue-300 bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-500 dark:border-gray-600 dark:text-gray-400 ">
            <FaCalendarAlt /> Cycle: {cycleLabel}
          </span>

          <div className="flex flex-wrap gap-2 text-xs font-semibold text-gray-600 dark:text-gray-400 sm:gap-4">
            <span className="flex items-center gap-1">
              <span className="inline-block h-3 w-3 rounded border border-green-400 bg-green-200 dark:border-green-300 dark:bg-green-500" />
              Present
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-3 w-3 rounded border border-red-400 bg-red-200 dark:border-red-300 dark:bg-red-500" />
              Absent
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-3 w-3 rounded border border-yellow-400 bg-yellow-200 dark:border-amber-300 dark:bg-amber-500" />
              Week Off
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-3 w-3 rounded border border-purple-400 bg-purple-200 dark:border-purple-500 dark:bg-purple-800" />
              Holiday
            </span>
          </div>
        </div>
      ) : null}

      {/* ── Loading ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-sm text-gray-500 dark:text-gray-400">
          <svg
            className="mr-3 h-5 w-5 animate-spin text-blue-500"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8z"
            />
          </svg>
          Loading attendance data…
        </div>
      ) : (
        <div className="scrollbar-thin scrollbar-thumb-blue-200 dark:scrollbar-thumb-blue-900 overflow-x-auto rounded-lg border border-gray-200 shadow-sm dark:border-gray-700">
          <table
            className="min-w-[600px] border-separate border-spacing-0 text-xs sm:min-w-full sm:text-sm"
            style={{ fontSize: 13, minWidth: "max-content", width: "100%" }}
          >
            <thead>
              {/* Row 1 – month group spans */}
              <tr>
                <th
                  className="border border-gray-300 bg-gray-200 px-3 py-3 text-center text-sm font-semibold text-gray-700 dark:border-navy-700 dark:bg-navy-800 dark:text-gray-100"
                  rowSpan={2}
                  style={{
                    minWidth: SNO_COL_WIDTH,
                    width: SNO_COL_WIDTH,
                    maxWidth: SNO_COL_WIDTH,
                    position: "sticky",
                    left: 0,
                    zIndex: 60,
                  }}
                >
                  S.No
                </th>
                <th
                  className="border border-gray-300 bg-gray-200 dark:border-navy-700 dark:bg-navy-800"
                  colSpan={2}
                  style={{
                    minWidth: EMP_ID_COL_WIDTH + EMP_DETAIL_COL_WIDTH,
                    position: "sticky",
                    left: EMP_ID_LEFT,
                    zIndex: 55,
                    boxShadow: "1px 0 0 0 rgba(203, 213, 225, 0.95)",
                  }}
                />
                {monthGroups.map((mg) => (
                  <th
                    key={mg.label}
                    className="border border-blue-400 bg-blue-300 px-2 py-2 text-center text-sm font-semibold text-blue-700 dark:border-blue-700 dark:bg-blue-700 dark:text-blue-300"
                    colSpan={mg.count}
                  >
                    {mg.label}
                  </th>
                ))}
              </tr>

              {/* Row 2 – fixed cols + day numbers */}
              <tr>
                <th
                  className="whitespace-nowrap border border-gray-300 bg-gray-100 px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:border-navy-700 dark:bg-navy-800 dark:text-gray-100"
                  style={{
                    minWidth: EMP_ID_COL_WIDTH,
                    width: EMP_ID_COL_WIDTH,
                    maxWidth: EMP_ID_COL_WIDTH,
                    position: "sticky",
                    left: EMP_ID_LEFT,
                    zIndex: 60,
                  }}
                >
                  EMP ID
                </th>
                <th
                  className="whitespace-nowrap border border-gray-300 bg-gray-100 px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:border-navy-700 dark:bg-navy-800 dark:text-gray-100"
                  style={{
                    minWidth: EMP_DETAIL_COL_WIDTH,
                    width: EMP_DETAIL_COL_WIDTH,
                    maxWidth: EMP_DETAIL_COL_WIDTH,
                    position: "sticky",
                    left: EMP_DETAIL_LEFT,
                    zIndex: 60,
                    boxShadow: "1px 0 0 0 rgba(203, 213, 225, 0.95)",
                  }}
                >
                  EMP DETAIL
                </th>
                {dates.map((dateStr) => {
                  const d = new Date(dateStr);
                  const isToday = dateStr === todayStr;
                  return (
                    <th
                      key={dateStr}
                      title={dateStr}
                      className={
                        isToday
                          ? "border border-blue-300 bg-blue-200 px-1 py-3 text-center text-sm font-bold text-blue-800 dark:border-blue-600 dark:bg-blue-800 dark:text-blue-200"
                          : "border border-gray-300 bg-gray-50 px-1 py-3 text-center text-sm font-semibold text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                      }
                      style={{ minWidth: 44, width: 44 }}
                    >
                      {d.getDate()}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {filteredMatrix.length === 0 ? (
                <tr>
                  <td
                    colSpan={3 + dates.length}
                    className="bg-white py-10 text-center text-sm text-gray-400 dark:bg-gray-900 dark:text-gray-500"
                  >
                    No attendance data found for this cycle ({cycleLabel}).
                  </td>
                </tr>
              ) : (
                filteredMatrix.map((emp, idx) => (
                  <tr
                    key={emp.emp_code}
                    className={
                      idx % 2 === 0
                        ? "bg-white hover:bg-blue-50 dark:bg-gray-900 dark:hover:bg-blue-900/20"
                        : "bg-gray-50 hover:bg-blue-50 dark:bg-gray-800/60 dark:hover:bg-blue-900/20"
                    }
                  >
                    {/* S.No */}
                    <td
                      className={`border border-gray-200 px-3 py-2.5 text-center text-sm font-semibold text-gray-500 dark:border-gray-700 dark:text-gray-400 ${
                        idx % 2 === 0
                          ? "bg-white dark:bg-navy-900"
                          : "bg-gray-50 dark:bg-navy-800"
                      }`}
                      style={{
                        minWidth: SNO_COL_WIDTH,
                        width: SNO_COL_WIDTH,
                        maxWidth: SNO_COL_WIDTH,
                        position: "sticky",
                        left: 0,
                        zIndex: 30,
                      }}
                    >
                      {idx + 1}
                    </td>

                    {/* EMP ID */}
                    <td
                      className={`whitespace-nowrap border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-800 dark:border-gray-700 dark:text-gray-200 ${
                        idx % 2 === 0
                          ? "bg-white dark:bg-navy-900"
                          : "bg-gray-50 dark:bg-navy-800"
                      }`}
                      style={{
                        minWidth: EMP_ID_COL_WIDTH,
                        width: EMP_ID_COL_WIDTH,
                        maxWidth: EMP_ID_COL_WIDTH,
                        position: "sticky",
                        left: EMP_ID_LEFT,
                        zIndex: 30,
                      }}
                    >
                      {emp.emp_code}
                    </td>

                    {/* EMP DETAIL */}
                    <td
                      className={`whitespace-nowrap border border-gray-200 px-4 py-2.5 text-sm dark:border-gray-700 ${
                        idx % 2 === 0
                          ? "bg-white dark:bg-navy-900"
                          : "bg-gray-50 dark:bg-navy-800"
                      }`}
                      style={{
                        minWidth: EMP_DETAIL_COL_WIDTH,
                        width: EMP_DETAIL_COL_WIDTH,
                        maxWidth: EMP_DETAIL_COL_WIDTH,
                        position: "sticky",
                        left: EMP_DETAIL_LEFT,
                        zIndex: 30,
                        boxShadow: "1px 0 0 0 rgba(203, 213, 225, 0.92)",
                      }}
                    >
                      <div className="flex items-center gap-3">
                        {emp.profile_picture ? (
                          <EmployeeProfileImage
                            src={emp.profile_picture}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600 dark:bg-blue-900/50 dark:text-blue-300">
                            {emp.employee_name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                        )}
                        <div className="flex flex-col">
                          <span
                            className="font-bold text-gray-800 dark:text-gray-100"
                            style={{ fontSize: 14 }}
                          >
                            {emp.employee_name}
                          </span>
                          <span
                            className="text-gray-500 dark:text-gray-400"
                            style={{ fontSize: 12 }}
                          >
                            {emp.department || "—"}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Attendance cells */}
                    {dates.map((dateStr) =>
                      renderCell(emp.attendance[dateStr], dateStr, emp.emp_code)
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AttendanceMatrix;
